import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { ownerApprovalAPI } from '../../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { getInitials, formatDate } from '../../lib/utils';
import { 
  Inbox, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Calendar, 
  Building2,
  UserCog,
  Shield,
  AlertTriangle,
  Loader2
} from 'lucide-react';

const ApprovalInbox = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved_this_month: 0, rejected_this_month: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  
  // Rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [inboxRes, statsRes] = await Promise.all([
        ownerApprovalAPI.getInbox(activeTab === 'all' ? 'all' : activeTab),
        ownerApprovalAPI.getStats()
      ]);
      setRequests(inboxRes.data.leave_requests || []);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load approval inbox');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (leaveId) => {
    setProcessing(leaveId);
    try {
      await ownerApprovalAPI.approveLeave(leaveId);
      toast.success('Leave request approved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve leave');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectClick = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedRequest) return;
    setProcessing(selectedRequest.id);
    try {
      await ownerApprovalAPI.rejectLeave(selectedRequest.id, rejectionReason);
      toast.success('Leave request rejected');
      setRejectDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject leave');
    } finally {
      setProcessing(null);
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <Badge className="bg-purple-100 text-purple-800 border-purple-200">
          <Shield className="h-3 w-3 mr-1" />
          Admin
        </Badge>
      );
    }
    if (role === 'manager') {
      return (
        <Badge className="bg-blue-100 text-blue-800 border-blue-200">
          <UserCog className="h-3 w-3 mr-1" />
          Manager
        </Badge>
      );
    }
    return null;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-800">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const calculateDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  if (loading && requests.length === 0) {
    return (
      <DashboardLayout title="Approval Inbox" subtitle="Manager &amp; Admin leave requests">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Approval Inbox" 
      subtitle="Review and approve manager &amp; admin leave requests"
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Approved (This Month)</p>
                <p className="text-2xl font-bold">{stats.approved_this_month}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rejected (This Month)</p>
                <p className="text-2xl font-bold">{stats.rejected_this_month}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <strong>Role-Based Approval:</strong> Manager and Admin leave requests are routed here for your approval. 
            Managers cannot approve leaves for other managers or admins. All approvals are logged with full audit trail.
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="h-4 w-4" />
            Pending ({stats.pending})
          </TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {requests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Inbox className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No {activeTab} leave requests</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <Card key={request.id} className="overflow-hidden" data-testid={`approval-card-${request.id}`}>
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      {/* Left: Employee Info */}
                      <div className="p-4 md:w-1/3 bg-muted/30 border-b md:border-b-0 md:border-r">
                        <div className="flex items-center gap-3 mb-3">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {getInitials(request.employee_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold">{request.employee_name}</p>
                            <p className="text-sm text-muted-foreground">{request.employee_email}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {getRoleBadge(request.employee_role)}
                          <Badge variant="outline" className="gap-1">
                            <Building2 className="h-3 w-3" />
                            {request.company_name}
                          </Badge>
                        </div>
                        {request.department_name && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Department: {request.department_name}
                          </p>
                        )}
                      </div>

                      {/* Right: Leave Details */}
                      <div className="p-4 flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{request.leave_type_name}</h4>
                              {getStatusBadge(request.status)}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {formatDate(request.start_date)} - {formatDate(request.end_date)}
                              </span>
                              <span className="font-medium text-primary">
                                {calculateDays(request.start_date, request.end_date)} days
                              </span>
                            </div>
                          </div>
                        </div>

                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          <strong>Reason:</strong> {request.reason}
                        </p>

                        {/* Approver Info (for processed requests) */}
                        {request.approver_info && (
                          <div className="text-xs text-muted-foreground mb-3 p-2 bg-muted/50 rounded">
                            {request.status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
                            <strong>{request.approver_info.name}</strong> ({request.approver_info.role})
                            {request.approved_at && ` on ${formatDate(request.approved_at)}`}
                            {request.rejection_reason && (
                              <div className="mt-1 text-red-600">
                                Reason: {request.rejection_reason}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Action Buttons (only for pending) */}
                        {request.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApprove(request.id)}
                              disabled={processing === request.id}
                              className="gap-1"
                              data-testid={`approve-btn-${request.id}`}
                            >
                              {processing === request.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <CheckCircle2 className="h-4 w-4" />
                              )}
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRejectClick(request)}
                              disabled={processing === request.id}
                              className="gap-1"
                              data-testid={`reject-btn-${request.id}`}
                            >
                              <XCircle className="h-4 w-4" />
                              Reject
                            </Button>
                          </div>
                        )}

                        <p className="text-xs text-muted-foreground mt-3">
                          Requested on {formatDate(request.created_at)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Leave Request</DialogTitle>
            <DialogDescription>
              You are rejecting {selectedRequest?.employee_name}&apos;s leave request.
              Please provide a reason (optional).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for rejection..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRejectConfirm}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Reject Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ApprovalInbox;
